import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  BookmarkIcon,
  EyeIcon,
  CalendarIcon,
  TagIcon,
  FolderIcon
} from '@heroicons/react/24/outline';

import { diaryService } from '../../services/diary';
import { format } from 'date-fns';

const AllEntries = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 12,
    folder: '',
    pinned: '',
    search: ''
  });
  const [pagination, setPagination] = useState(null);
  const [folders, setFolders] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [entriesResponse, foldersData] = await Promise.all([
          diaryService.getAllEntries(filters),
          diaryService.getFolders()
        ]);
        
        setEntries(entriesResponse.data.entries);
        setPagination(entriesResponse.data.pagination);
        setFolders(foldersData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching entries:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const getMoodEmoji = (mood) => {
    const moodMap = {
      'happy': '😊',
      'sad': '😢',
      'excited': '😍',
      'angry': '😡',
      'peaceful': '😌',
      'anxious': '😰',
      'thoughtful': '🤔',
      'tired': '😴'
    };
    return moodMap[mood?.toLowerCase()] || mood || '📝';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            className="flex items-center justify-between"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div>
              <h1 className="text-4xl font-serif font-bold mb-2">
                All Entries
              </h1>
              <p className="text-primary-100 text-lg">
                {pagination?.totalEntries || 0} entries in your journal
              </p>
            </div>
            <Link
              to="/entries/new"
              className="bg-white text-primary-600 px-6 py-3 rounded-full font-medium hover:bg-primary-50 transition-colors flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>New Entry</span>
            </Link>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <motion.div
          className="card p-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search entries..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Folder Filter */}
            <select
              value={filters.folder}
              onChange={(e) => handleFilterChange('folder', e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Folders</option>
              {folders.map(folder => (
                <option key={folder} value={folder}>{folder}</option>
              ))}
            </select>

            {/* Pinned Filter */}
            <select
              value={filters.pinned}
              onChange={(e) => handleFilterChange('pinned', e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Entries</option>
              <option value="true">Pinned Only</option>
              <option value="false">Not Pinned</option>
            </select>
          </div>
        </motion.div>

        {/* Entries Grid */}
        {entries.length > 0 ? (
          <>
            <motion.div
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <AnimatePresence>
                {entries.map((entry, index) => (
                  <motion.div
                    key={entry._id}
                    className="card p-6 hover:shadow-xl transition-all duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                  >
                    <Link to={`/entries/${entry._id}`} className="block">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          {entry.mood && (
                            <span className="text-2xl">
                              {getMoodEmoji(entry.mood)}
                            </span>
                          )}
                          {entry.isPinned && (
                            <BookmarkIcon className="h-4 w-4 text-red-500 fill-current" />
                          )}
                        </div>
                        <span className="text-xs text-gray-500">
                          {format(new Date(entry.createdAt), 'MMM d')}
                        </span>
                      </div>

                      <h3 className="text-lg font-serif font-semibold text-ink-black mb-2 line-clamp-2">
                        {entry.title}
                      </h3>

                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                        {entry.content.replace(/<[^>]*>/g, '').substring(0, 120)}...
                      </p>

                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-1">
                            <EyeIcon className="h-3 w-3" />
                            <span>{entry.viewCount}</span>
                          </div>
                          {entry.folder && (
                            <div className="flex items-center space-x-1">
                              <FolderIcon className="h-3 w-3" />
                              <span>{entry.folder}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-1">
                          {entry.tags.slice(0, 2).map(tag => (
                            <span
                              key={tag}
                              className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full"
                            >
                              #{tag}
                            </span>
                          ))}
                          {entry.tags.length > 2 && (
                            <span className="text-gray-400">
                              +{entry.tags.length - 2}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <motion.div
                className="flex justify-center space-x-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                {pagination.hasPrev && (
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Previous
                  </button>
                )}
                
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      page === pagination.currentPage
                        ? 'bg-primary-600 text-white'
                        : 'border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                
                {pagination.hasNext && (
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Next
                  </button>
                )}
              </motion.div>
            )}
          </>
        ) : (
          <motion.div
            className="card p-12 text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              No entries found
            </h3>
            <p className="text-gray-500 mb-6">
              {filters.search || filters.folder || filters.pinned
                ? 'Try adjusting your filters to see more entries'
                : 'Start your journaling journey by creating your first entry'
              }
            </p>
            <Link to="/entries/new" className="btn-primary">
              <PlusIcon className="h-5 w-5 mr-2" />
              Create First Entry
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AllEntries;
